import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { AdTopBanner, AdInContent, AdBottomBanner } from '../components/AdSense';

const SalesTaxCalculatorPage = () => {
  const [calcMode, setCalcMode] = useState('add'); // 'add' or 'reverse'
  const [beforeTax, setBeforeTax] = useState('100');
  const [afterTax, setAfterTax] = useState('');
  const [taxRate, setTaxRate] = useState('7');
  const [result, setResult] = useState(null);

  const calculate = () => {
    const rate = (parseFloat(taxRate) || 0) / 100;
    
    if (calcMode === 'add') {
      const price = parseFloat(beforeTax) || 0;
      const tax = price * rate;
      const total = price + tax;
      setResult({ beforeTax: price, taxAmount: tax, afterTax: total, rate: taxRate });
    } else {
      const total = parseFloat(afterTax) || 0;
      const price = total / (1 + rate);
      const tax = total - price;
      setResult({ beforeTax: price, taxAmount: tax, afterTax: total, rate: taxRate });
    }
  };

  const formatCurrency = (num) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);

  const stateRates = [
    { state: 'California', rate: '7.25%' }, { state: 'Texas', rate: '6.25%' },
    { state: 'New York', rate: '4%' }, { state: 'Florida', rate: '6%' },
    { state: 'Illinois', rate: '6.25%' }, { state: 'Pennsylvania', rate: '6%' },
    { state: 'Ohio', rate: '5.75%' }, { state: 'Georgia', rate: '4%' },
    { state: 'North Carolina', rate: '4.75%' }, { state: 'Michigan', rate: '6%' }
  ];

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <AdTopBanner />
          
          <nav className="text-sm text-gray-500 mb-4">
            <Link to="/home" className="text-cyan-400 hover:text-cyan-300">Home</Link>
            <span className="mx-2">›</span>
            <span className="text-gray-400">Sales Tax Calculator</span>
          </nav>

          <h1 className="text-3xl font-bold text-white mb-4">Sales Tax Calculator</h1>
          <p className="text-gray-400 mb-6">
            Calculate sales tax on purchases or reverse-calculate the price before tax was applied.
          </p>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <div className="flex border-b border-[#2a2a2a] mb-6">
              <button onClick={() => setCalcMode('add')}
                className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${calcMode === 'add' ? 'border-cyan-500 text-cyan-400' : 'border-transparent text-gray-400'}`}>
                Add Tax
              </button>
              <button onClick={() => setCalcMode('reverse')}
                className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${calcMode === 'reverse' ? 'border-cyan-500 text-cyan-400' : 'border-transparent text-gray-400'}`}>
                Reverse Tax
              </button>
            </div>

            <div className="space-y-4">
              {calcMode === 'add' ? (
                <div className="flex flex-wrap items-center gap-4">
                  <label className="text-gray-300 font-medium w-32">Before Tax Price</label>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-400">$</span>
                    <Input type="number" value={beforeTax} onChange={(e) => setBeforeTax(e.target.value)}
                      className="w-32 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-4">
                  <label className="text-gray-300 font-medium w-32">After Tax Price</label>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-400">$</span>
                    <Input type="number" value={afterTax} onChange={(e) => setAfterTax(e.target.value)}
                      className="w-32 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                  </div>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-32">Sales Tax Rate</label>
                <div className="flex items-center gap-1">
                  <Input type="number" step="0.01" value={taxRate} onChange={(e) => setTaxRate(e.target.value)}
                    className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                  <span className="text-gray-400">%</span>
                </div>
              </div>
            </div>

            <Button onClick={calculate} className="mt-6 bg-cyan-600 hover:bg-cyan-500 text-white px-8">
              Calculate
            </Button>

            {result && (
              <div className="mt-6 p-5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-xl">
                <div className="space-y-3">
                  <div className="flex justify-between p-3 bg-[#1a1a1a] rounded-lg">
                    <span className="text-gray-400">Before Tax Price</span>
                    <span className="font-semibold text-white">{formatCurrency(result.beforeTax)}</span>
                  </div>
                  <div className="flex justify-between p-3 bg-[#1a1a1a] rounded-lg">
                    <span className="text-gray-400">Sales Tax ({result.rate}%)</span>
                    <span className="font-semibold text-cyan-400">+ {formatCurrency(result.taxAmount)}</span>
                  </div>
                  <div className="flex justify-between p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                    <span className="text-cyan-400 font-medium">After Tax Price</span>
                    <span className="font-bold text-cyan-400 text-lg">{formatCurrency(result.afterTax)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <AdInContent />

          <div className="mt-10">
            <h2 className="text-2xl font-bold text-white mb-4">US State Sales Tax Rates</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-[#2a2a2a]"><th className="text-left py-2 px-4 text-gray-400">State</th><th className="text-right py-2 px-4 text-gray-400">Rate</th></tr></thead>
                  <tbody>
                    {stateRates.slice(0, 5).map((s, i) => (
                      <tr key={i} className="border-b border-[#2a2a2a]/50">
                        <td className="py-2 px-4 text-gray-300">{s.state}</td>
                        <td className="py-2 px-4 text-right text-white font-medium">{s.rate}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-[#2a2a2a]"><th className="text-left py-2 px-4 text-gray-400">State</th><th className="text-right py-2 px-4 text-gray-400">Rate</th></tr></thead>
                  <tbody>
                    {stateRates.slice(5, 10).map((s, i) => (
                      <tr key={i} className="border-b border-[#2a2a2a]/50">
                        <td className="py-2 px-4 text-gray-300">{s.state}</td>
                        <td className="py-2 px-4 text-right text-white font-medium">{s.rate}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="text-gray-500 text-sm mt-2">Note: Rates shown are state-level only. Local taxes may apply.</p>
          </div>

          <div className="mt-10 p-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
            <h3 className="text-lg font-semibold text-white mb-4">Financial Calculators</h3>
            <div className="flex flex-wrap gap-2">
              {[{ name: 'Loan', path: '/loan-calculator' }, { name: 'Mortgage', path: '/mortgage-calculator' }, { name: 'Interest', path: '/interest-calculator' }, { name: 'Salary', path: '/salary-calculator' }].map((calc) => (
                <Link key={calc.path} to={calc.path} className="px-3 py-1.5 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-cyan-400 hover:text-cyan-300 text-sm">{calc.name}</Link>
              ))}
            </div>
          </div>
          <AdBottomBanner />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SalesTaxCalculatorPage;
